import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { HTMLAttributes, useRef } from "react";

import { PropsWithDataQaid } from "src/types/componentProps";
import {
  BottomSheetContainer,
  BottomSheetContentAutoFlow,
  BottomSheetDialog,
  BottomSheetOverlay,
  CloseButton,
  DialogBody,
  DialogHeader
} from "./BottomSheet.styled";

export type BottomSheetProps = HTMLAttributes<HTMLDivElement> &
  PropsWithDataQaid<{
    /**
     * set bottom seat max width
     *
     * @type {"inherit" | "default" | "block" }
     */
    size?: "inherit" | "default" | "block";

    /**
     * Set if bottomsheet shown
     */
    show?: boolean;

    /**
     * close function to hide bottomsheet
     */
    close?: () => void;

    /**
     * whether bottomsheet closed when overlay clicked
     */
    closeOnOverlay?: boolean;

    /**
     * flag to show header or not
     */
    withoutHeader?: boolean;
  }>;

const defaultProps = {
  show: false,
  close: () => {},
  closeOnOverlay: false,
  withoutHeader: false,
  "data-qaid": "qa-bottom-sheet",
  size: "default"
};

export function BottomSheet(props: BottomSheetProps) {
  const { children, show, withoutHeader, close, className, size, closeOnOverlay, "data-qaid": dataQaid } = props;
  const containerRef = useRef<HTMLDivElement>();
  const containerClassName = classNames(
    "c-bottom-sheet",
    show ? "c-bottom-sheet-show" : "c-bottom-sheet-hide",
    className
  );

  function overlayClick() {
    if (closeOnOverlay) {
      close();
    }
  }

  const SIZE = {
    default: { "--c-max-width": "512px" },
    block: { "--c-max-width": "none" },
    inherit: { "--c-max-width": containerRef.current?.parentElement?.clientWidth + "px" }
  };

  return (
    <BottomSheetContainer
      data-testid={dataQaid}
      style={SIZE[size] as unknown}
      ref={containerRef}
      className={containerClassName}
    >
      <BottomSheetOverlay data-testid={`${dataQaid}-overlay`} onClick={overlayClick} />
      <BottomSheetDialog
        className="c-bottom-sheet__dialog"
        data-testid="qa-bottom-sheet-dialog"
        data-qaid={`${dataQaid}-dialog`}
      >
        {!withoutHeader && (
          <DialogHeader
            className="c-bottom-sheet__header"
            data-testid="qa-bottom-sheet-header"
            data-qaid={`${dataQaid}-header`}
          >
            <CloseButton onClick={close}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
          </DialogHeader>
        )}
        <DialogBody className="c-bottom-sheet__body" data-testid="qa-bottom-sheet-body" data-qaid={`${dataQaid}-body`}>
          {children}
        </DialogBody>
      </BottomSheetDialog>
    </BottomSheetContainer>
  );
}

BottomSheet.ContentAutoFlow = BottomSheetContentAutoFlow;
BottomSheet.defaultProps = defaultProps;

export default BottomSheet;
